import { Injectable } from '@angular/core';
import { ProcessErrorCode } from 'src/app/ajs-upgraded-providers';
import { Display } from 'src/app/api/services/display';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { DisplayService } from './display.service';
import { FirmwareUpdateService } from './firmware-update.service';

export type PlayerActionConfirmResult =
  | { status: 'success' }
  | { status: 'cancelled' }
  | { status: 'license_required' }
  | { status: 'error'; message: string };

@Injectable({
  providedIn: 'root',
})
export class PlayerActionsService {
  controlsError: string = '';

  public static ACTION_TYPES = {
    RESTART: 'restart',
    REBOOT: 'reboot',
    UPDATE_FIRMWARE: 'updateFirmware',
    POWER_ON: 'poweron',
    POWER_OFF: 'poweroff',
    LAUNCH: 'launch',
  };

  public static KEY_OPERATION_MODE_TYPES = {
    BLOCK_ALL: 'blockAll',
    NORMAL: 'normal',
  };

  constructor(
    private displayApiService: DisplayApiService,
    private modalService: ModalService,
    private processErrorCode: ProcessErrorCode,
    private trackerService: TrackerService,
    private displayService: DisplayService,
    private firmwareUpdateService: FirmwareUpdateService,
  ) {}

  _restart(displayId, displayName) {
    if (!displayId) {
      return;
    }

    return this.displayApiService.restart(displayId).then(() => {
      this.trackerService.displayEvent(
        'Display Restarted',
        displayId,
        displayName,
      );
    });
  }

  private _reboot(displayId, displayName) {
    if (!displayId) {
      return;
    }

    return this.displayApiService.reboot(displayId).then(() => {
      this.trackerService.displayEvent(
        'Display Rebooted',
        displayId,
        displayName,
      );
    });
  }

  private _updateFirmware(displayId, displayName, firmwareUrl?) {
    if (!displayId) {
      return;
    }

    if (!firmwareUrl) {
      return Promise.reject(new Error('Firmware URL is required'));
    }

    // Use the FirmwareUpdateService which handles the Primus connection
    // and communicates with Core to send the firmware-upgrade-request message to Viewer
    this.trackerService.displayEvent(
      'Display Firmware Update Requested',
      displayId,
      displayName,
    );

    return this.firmwareUpdateService
      .requestFirmwareUpdate(displayId, displayName, firmwareUrl)
      .then(() => {
        this.trackerService.displayEvent(
          'Display Firmware Update Complete',
          displayId,
          displayName,
        );
      })
      .catch((error) => {
        this.trackerService.displayEvent(
          'Display Firmware Update Failed',
          displayId,
          displayName,
        );
        throw error;
      });
  }

  restartByObject(display) {
    return this._restart(display.id, display.name);
  }

  rebootByObject(display) {
    return this._reboot(display.id, display.name);
  }

  updateFirmwareByObject(display, firmwareUrl?) {
    return this._updateFirmware(display.id, display.name, firmwareUrl);
  }

  sendNotification(
    displayId: string | undefined,
    displayName: string | undefined,
    type: string,
  ): Promise<void> {
    if (!displayId) {
      return Promise.reject(
        new Error('Display id is required to send a notification'),
      );
    }

    return this.displayApiService.sendNotification(displayId, type).then(() => {
      this.trackerService.displayEvent(
        'Display Notification Sent',
        displayId,
        displayName,
      );
    });
  }

  sendNotificationByObject(display: Display, type: string): Promise<void> {
    return this.sendNotification(display?.id, display?.name, type);
  }

  private _confirmScreenPowerOn(
    displayId,
    displayName,
  ): Promise<PlayerActionConfirmResult> {
    return this.modalService
      .confirm(
        'Screen Power On?',
        'The screen will be powered on. Do you wish to proceed?',
        'Yes',
        'No',
      )
      .then(() => {
        const pending = this.sendNotification(
          displayId,
          displayName,
          PlayerActionsService.ACTION_TYPES.POWER_ON,
        );
        if (!pending) {
          return {
            status: 'error' as const,
            message: this.processErrorCode(null),
          };
        }
        return pending
          .then(() => ({ status: 'success' as const }))
          .catch((e) => ({
            status: 'error' as const,
            message: this.processErrorCode(e),
          }));
      })
      .catch(() => ({ status: 'cancelled' as const }));
  }

  private _confirmScreenPowerOff(
    displayId,
    displayName,
  ): Promise<PlayerActionConfirmResult> {
    return this.modalService
      .confirm(
        'Screen Power Off?',
        'The screen will be powered off. Do you wish to proceed?',
        'Yes',
        'No',
      )
      .then(() => {
        const pending = this.sendNotification(
          displayId,
          displayName,
          PlayerActionsService.ACTION_TYPES.POWER_OFF,
        );
        if (!pending) {
          return {
            status: 'error' as const,
            message: this.processErrorCode(null),
          };
        }
        return pending
          .then(() => ({ status: 'success' as const }))
          .catch((e) => ({
            status: 'error' as const,
            message: this.processErrorCode(e),
          }));
      })
      .catch(() => ({ status: 'cancelled' as const }));
  }

  private _confirmLaunchPlayer(
    displayId,
    displayName,
  ): Promise<PlayerActionConfirmResult> {
    return this.modalService
      .confirm(
        'Launch Rise Player?',
        'Rise Player will be launched on the device. Do you wish to proceed?',
        'Yes',
        'No',
      )
      .then(() => {
        const pending = this.sendNotification(
          displayId,
          displayName,
          PlayerActionsService.ACTION_TYPES.LAUNCH,
        );
        if (!pending) {
          return {
            status: 'error' as const,
            message: this.processErrorCode(null),
          };
        }
        return pending
          .then(() => ({ status: 'success' as const }))
          .catch((e) => ({
            status: 'error' as const,
            message: this.processErrorCode(e),
          }));
      })
      .catch(() => ({ status: 'cancelled' as const }));
  }

  private _confirmRestart(
    displayId,
    displayName,
  ): Promise<PlayerActionConfirmResult> {
    return this.modalService
      .confirm(
        'Restart Rise Player?',
        'Rise Player will restart and the content showing on your display will be interrupted. Do you wish to proceed?',
        'Yes',
        'No',
      )
      .then(() => {
        const pending = this._restart(displayId, displayName);
        if (!pending) {
          return {
            status: 'error' as const,
            message: this.processErrorCode(null),
          };
        }
        return pending
          .then(() => ({ status: 'success' as const }))
          .catch((e) => ({
            status: 'error' as const,
            message: this.processErrorCode(e),
          }));
      })
      .catch(() => ({ status: 'cancelled' as const }));
  }

  private _confirmReboot(
    displayId,
    displayName,
  ): Promise<PlayerActionConfirmResult> {
    return this.modalService
      .confirm(
        'Reboot media player?',
        'The media player will reboot and the content showing on your display will be interrupted. Do you wish to proceed?',
        'Yes',
        'No',
      )
      .then(() => {
        const pending = this._reboot(displayId, displayName);
        if (!pending) {
          return {
            status: 'error' as const,
            message: this.processErrorCode(null),
          };
        }
        return pending
          .then(() => ({ status: 'success' as const }))
          .catch((e) => ({
            status: 'error' as const,
            message: this.processErrorCode(e),
          }));
      })
      .catch(() => ({ status: 'cancelled' as const }));
  }

  private async _confirmUpdateFirmware(
    displayId,
    displayName,
  ): Promise<PlayerActionConfirmResult> {
    try {
      const modalResult = await this.modalService.showFirmwareUpdateModal({
        displayName: displayName,
        isMultiple: false,
      });
      if (!modalResult?.firmwareUrl) {
        return { status: 'cancelled' };
      }

      const pending = this._updateFirmware(
        displayId,
        displayName,
        modalResult.firmwareUrl,
      );
      if (!pending) {
        return {
          status: 'error',
          message: this.processErrorCode(null),
        };
      }
      try {
        await pending;
        return { status: 'success' };
      } catch (e) {
        return {
          status: 'error',
          message: this.processErrorCode(e),
        };
      }
    } catch {
      return { status: 'cancelled' };
    }
  }

  confirmWithResult(
    displayId,
    displayName,
    mode,
    display?: Display,
  ): Promise<PlayerActionConfirmResult> {
    if (this.displayService.showLicenseRequired(display)) {
      return Promise.resolve({ status: 'license_required' });
    }

    if (mode === PlayerActionsService.ACTION_TYPES.REBOOT) {
      return this._confirmReboot(displayId, displayName);
    } else if (mode === PlayerActionsService.ACTION_TYPES.RESTART) {
      return this._confirmRestart(displayId, displayName);
    } else if (mode === PlayerActionsService.ACTION_TYPES.LAUNCH) {
      return this._confirmLaunchPlayer(displayId, displayName);
    } else if (mode === PlayerActionsService.ACTION_TYPES.UPDATE_FIRMWARE) {
      return this._confirmUpdateFirmware(displayId, displayName);
    } else if (mode === PlayerActionsService.ACTION_TYPES.POWER_ON) {
      return this._confirmScreenPowerOn(displayId, displayName);
    } else if (mode === PlayerActionsService.ACTION_TYPES.POWER_OFF) {
      return this._confirmScreenPowerOff(displayId, displayName);
    }

    return Promise.resolve({ status: 'cancelled' });
  }

  confirm(displayId, displayName, mode): Promise<boolean> {
    return this.confirmWithResult(displayId, displayName, mode).then(
      (result) => {
        if (result.status === 'error') {
          this.controlsError = result.message;
        } else if (
          result.status === 'success' ||
          result.status === 'license_required'
        ) {
          this.controlsError = '';
        }
        return result.status === 'success';
      },
    );
  }
}
